import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Auth, authState } from '@angular/fire/auth';
import { Subscription } from 'rxjs';
import { NotificationService, Notification, NotificationType } from '../../../core/services/notification.service';
import { formatTemplateNameForDisplay } from '../../../core/utils/template-name.utils';
import { trigger, transition, style, animate } from '@angular/animations';

interface BannerNotification extends Notification {
    dismissing?: boolean;  // tracks slide-out animation
}

@Component({
    selector: 'app-notification-banner',
    templateUrl: './notification-banner.component.html',
    styleUrls: ['./notification-banner.component.scss'],
    standalone: true,
    imports: [CommonModule],
    animations: [
        trigger('slideIn', [
            transition(':enter', [
                style({ transform: 'translateX(100%)', opacity: 0 }),
                animate('300ms ease-out', style({ transform: 'translateX(0)', opacity: 1 })),
            ]),
            transition(':leave', [
                animate('300ms ease-in', style({ transform: 'translateX(100%)', opacity: 0 })),
            ]),
        ]),
    ],
})
export class NotificationBannerComponent implements OnInit, OnDestroy {
    private auth = inject(Auth);
    private notificationService = inject(NotificationService);
    private router = inject(Router);

    banners: BannerNotification[] = [];
    private seenIds = new Set<string>(); // already-shown notification IDs
    private notificationsSub?: Subscription;
    private authSub?: Subscription;
    private autoDismissTimers = new Map<string, ReturnType<typeof setTimeout>>();
    private initialLoad = true; // skip toasts for notifications that exist on page load
    private authState$ = authState(this.auth); // Create observable in injection context

    ngOnInit(): void {
        this.authSub = this.authState$.subscribe(user => {
            if (user) {
                this.listenForNotifications(user.uid);
            } else {
                this.cleanup();
            }
        });
    }

    ngOnDestroy(): void {
        this.cleanup();
        this.authSub?.unsubscribe();
    }

    private cleanup(): void {
        this.notificationsSub?.unsubscribe();
        this.autoDismissTimers.forEach(t => clearTimeout(t));
        this.autoDismissTimers.clear();
        this.banners = [];
        this.seenIds.clear();
        this.initialLoad = true;
    }

    private listenForNotifications(userId: string): void {
        this.notificationsSub?.unsubscribe();
        this.initialLoad = true;

        this.notificationsSub = this.notificationService.getUserNotifications(userId, 50).subscribe({
            next: (notifications) => {
                if (this.initialLoad) {
                    // On first load, record all current IDs so we don't toast them
                    notifications.forEach(n => { if (n.id) this.seenIds.add(n.id); });
                    this.initialLoad = false;
                    return;
                }

                // Find new unread, non-silent notifications we haven't shown yet
                const newOnes = notifications.filter(n =>
                    n.id &&
                    !n.isRead &&
                    !n.silent &&
                    !this.seenIds.has(n.id)
                );

                for (const n of newOnes) {
                    if (!n.id) continue;
                    this.seenIds.add(n.id);

                    // Enrich with correct icon/color from the service
                    const style = this.notificationService.getNotificationStyle(n.type);
                    const banner: BannerNotification = {
                        ...n,
                        icon: n.icon || style.icon,
                        iconColor: n.iconColor || style.iconColor,
                    };

                    this.banners.push(banner);

                    // Auto-dismiss after 8 seconds
                    const timer = setTimeout(() => this.dismiss(banner), 8000);
                    this.autoDismissTimers.set(n.id, timer);
                }
            },
        });
    }

    formatNotificationText(text: string): string {
        if (!text) return text;
        return text.replace(/"([a-z0-9_]+)"/g, (_match, name) => `"${formatTemplateNameForDisplay(name)}"`);
    }

    /** Click handler – mark as read and navigate */
    onBannerClick(banner: BannerNotification): void {
        // Clear auto-dismiss timer
        if (banner.id) {
            const timer = this.autoDismissTimers.get(banner.id);
            if (timer) {
                clearTimeout(timer);
                this.autoDismissTimers.delete(banner.id);
            }

            // Mark as read
            this.notificationService.markAsRead(banner.id).catch(err =>
                console.error('Error marking notification as read:', err)
            );
        }

        // Navigate to the appropriate page
        const route = this.getRouteForNotification(banner);
        if (route) {
            this.router.navigateByUrl(route);
        }

        // Remove banner
        this.removeBanner(banner);
    }

    /** Close button – just dismiss without navigating */
    onDismiss(event: MouseEvent, banner: BannerNotification): void {
        event.stopPropagation();
        this.dismiss(banner);
    }

    private dismiss(banner: BannerNotification): void {
        if (banner.id) {
            const timer = this.autoDismissTimers.get(banner.id);
            if (timer) {
                clearTimeout(timer);
                this.autoDismissTimers.delete(banner.id);
            }
        }
        this.removeBanner(banner);
    }

    private removeBanner(banner: BannerNotification): void {
        const idx = this.banners.indexOf(banner);
        if (idx > -1) {
            this.banners.splice(idx, 1);
        }
    }

    /** Map notification type/link to in-app route */
    private getRouteForNotification(notification: Notification): string | null {
        // If the notification already has a link, use it
        if (notification.link) {
            return notification.link;
        }

        // Otherwise, infer route from notification type
        const typeRoutes: Partial<Record<NotificationType, string>> = {
            // Messaging
            message_sent: '/history',
            message_failed: '/history',

            // Templates
            template_approved: '/whatsapp/templates',
            template_rejected: '/whatsapp/templates',

            // WhatsApp numbers
            whatsapp_connected: '/whatsapp/phone-numbers',
            whatsapp_disconnected: '/whatsapp/phone-numbers',
            number_verified: '/whatsapp/phone-numbers',
            number_unverified: '/whatsapp/phone-numbers',

            // Billing / subscription
            trial_expiring: '/organization/subscription',
            trial_expired: '/organization/subscription',
            credits_low: '/organization/subscription',
            credits_depleted: '/organization/subscription',
            invoice_due: '/organization/invoices',
            invoice_overdue: '/organization/invoices',
            payment_received: '/organization/invoices',

            // Users
            user_added: '/organization/users',
            user_removed: '/organization/users',

            // PCO sync
            pco_sync_complete: '/organization/settings',
            pco_sync_failed: '/organization/settings',

            // Chat / new messages
            new_message: 'conversations',

            // System
            system_announcement: '/dashboard',
        };

        return typeRoutes[notification.type] || null;
    }

    /** Get the CSS class for the icon background */
    getIconBgClass(color: string): string {
        return `bg-${color}`;
    }
}
